import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Chip, InputAdornment, MenuItem, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  PersonSearchOutlined as PeopleIcon,
} from '@mui/icons-material';
import { cached, putCached, cachedCatalog, putCachedCatalog } from './pipelineCache';
import { callState, isPressing } from './callTime';
import ScheduleCall from './ScheduleCall';
import HiringService, {
  Candidate, CandidateOutcome, CandidateStage, Checkpoint, CheckpointStatus,
} from '../../api/services/hiring.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import DataTable, { Column } from '../../components/ui/DataTable';
import { EmptyState } from '../../components/ui/States';
import { status as statusTokens } from '../../theme';
import { Star as StarIcon } from '@mui/icons-material';
import HiringPanel from './HiringPanel';
import useStalled, { refreshStalled } from './useStalled';
import EditableCell from './EditableCell';
import { useAuthStore } from '../../store/authStore';
import CandidateGrid from './CandidateGrid';
import UploadCheckpointDialog from './UploadCheckpointDialog';
import {
  SOURCE_LABEL, MOVE_LABEL, moveFor, todoSentence, checkpointLabel, nextTone, dueWords,
  withWhom, waitingWords,
  OutcomeChip, StageMeter, IdleDays, MoveDot, candidateName, formatDate,
} from './hiringUI';

// The pipeline as a table.
//
// History, because the file has now argued both sides. The first grid was
// the spreadsheet's: fifteen checkpoint columns, 3,000 pixels wide. The
// list era replaced all of that with one meter, on the argument that the
// columns that mattered were the ones nobody could see. The owner used
// both and asked for the grid back — editable and compact — and was
// right: the meter says HOW FAR someone is, but only the cells say WHAT
// is done, and the tracker this replaced was lived in precisely because
// every step was one glanceable, clickable cell. So All candidates is
// CandidateGrid (34px glyph columns, sticky identity, scrolls in its own
// container), and the meter columns below survive on the Closed view.
// To do stays a list: its job is the sentence, not the cells.

type Mode = 'all' | 'follow-up' | 'closed';

/**
 * Has the interview happened? A booked time that has been kept is a
 * record, not an appointment — so nothing on this screen calls it missed.
 */
const interviewed = (row: Candidate): boolean =>
  row.checkpoints?.interview_held?.status === 'done';

/** The orders the to-do list can be read in. */
type TodoSort = 'urgent' | 'late' | 'due' | 'quiet' | 'name';

const TODO_SORTS: Array<{ value: TodoSort; label: string }> = [
  { value: 'urgent', label: 'Most urgent' },
  { value: 'late', label: 'Most overdue' },
  { value: 'due', label: 'Due soonest' },
  { value: 'quiet', label: 'Quietest' },
  { value: 'name', label: 'Name' },
];

type TodoOwner = 'me' | 'all' | 'us' | 'them' | 'third_party';

// "You" is the PERSON, and the list opens on it: the question somebody
// brings to this page is "what am I holding up", and "us" answered it for
// the whole company — a teammate's candidates included.
const TODO_OWNERS: Array<{ value: TodoOwner; label: string }> = [
  { value: 'me', label: 'You' },
  { value: 'us', label: 'Your team' },
  { value: 'them', label: 'The candidate' },
  { value: 'third_party', label: 'A lab or FedEx' },
  { value: 'all', label: 'Anybody' },
];

/**
 * Whether this row is held up by the person looking at it: the next move
 * is ours (a booked call always is), and the candidate is theirs — or
 * nobody's, because an unowned candidate is chased by whoever opens the
 * list, or by nobody.
 */
const waitingOnMe = (r: Candidate, me: string | undefined): boolean =>
  (r.nextAction?.owedBy === 'us' || !!r.scheduledCallAt)
  && (!r.ownerAccountId || r.ownerAccountId === me);

const ownerMatches = (r: Candidate, owner: TodoOwner, me: string | undefined): boolean => {
  if (owner === 'all') return true;
  if (owner === 'me') return waitingOnMe(r, me);
  // The next action is ours whenever there is anything ours to do, so a
  // pack out for signature lives in `waitingOn` — and "the candidate"
  // must still find it there.
  return r.nextAction?.owedBy === owner
    || !!r.nextAction?.waitingOn?.some((w) => w.owedBy === owner);
};

const TITLES: Record<Mode, { title: string; subtitle: string }> = {
  all: {
    title: 'Candidates',
    subtitle: 'Everyone currently being considered',
  },
  'follow-up': {
    title: 'To do',
    // Not "whose next step is yours" any more: the list carries what is
    // sitting with the candidate and what is sitting at a lab too, and a
    // person waiting on somebody else is still a person to chase.
    subtitle: 'Every open candidate with something outstanding',
  },
  closed: {
    title: 'Closed',
    subtitle: 'Pipelines that ended — hired, declined, withdrawn or gone quiet',
  },
};

const CandidateList: React.FC<{ mode: Mode }> = ({ mode }) => {
  const navigate = useNavigate();
  const { ids: stalledIds } = useStalled();
  // Seeded from the last load of this same view, so coming back to the
  // pipeline paints the table immediately and the refresh happens behind
  // it. A screen that blanks itself on every visit reads as slow even when
  // the request is 90ms — which, measured, is what it is.
  const [rows, setRows] = useState<Candidate[]>(() => cached(mode).rows);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(() => cached(mode).rows.length === 0);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  // The grid filters stage client-side now; Closed never had the
  // dropdown, so this is a constant until somebody gives Closed one.
  const stage: CandidateStage | 'all' = 'all';
  const [catalog, setCatalog] = useState<Checkpoint[]>(() => cachedCatalog());
  const [uploadFor, setUploadFor] = useState<{ row: Candidate; checkpoint: Checkpoint } | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  /** What was just copied, so the row's click has an answer on screen. */
  const [copied, setCopied] = useState<string | null>(null);

  const load = useCallback(async () => {
    // Only the FIRST load of a view shows a loading state. A refresh
    // behind a table that is already on screen must not take it away.
    setLoading(cached(mode).rows.length === 0);
    try {
      const result = await HiringService.list({
        includeClosed: mode === 'closed',
        outcome: mode === 'closed' ? 'all' : 'open',
        // The grid filters name and stage client-side in its own headers;
        // only Closed still asks the server.
        stage: mode === 'closed' && stage !== 'all' ? stage : undefined,
        search: mode === 'closed' ? (search.trim() || undefined) : undefined,
        pageSize: 500,
        // The grid draws every cell; the other two views have nowhere to
        // put them.
        checkpoints: mode === 'all',
      });
      const next = mode === 'closed'
        ? result.items.filter((c) => c.outcome !== 'open')
        : result.items;
      putCached(mode, next);
      setRows(next);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Could not load the pipeline.');
    } finally {
      setLoading(false);
    }
  }, [mode, stage, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);

  return () => clearTimeout(timer);
  }, [load, search]);

  // The columns, once. The catalog changes when the company's process
  // does, which is not something that happens between page visits.
  useEffect(() => {
    if (mode !== 'all') return;
    // The catalog changes when the company's process does, which is not
    // something that happens between page visits — so it is fetched once
    // per session and reused, rather than blocking the first paint of
    // every visit on a round trip that returns the same 25 rows.
    if (cachedCatalog().length > 0) return;
    HiringService.catalog()
      .then((result) => { putCachedCatalog(result.catalog); setCatalog(result.catalog); })
      .catch(() => setCatalog([]));
  }, [mode]);

  /**
   * Edit in the table rather than opening the record.
   *
   * Optimistic, because a list you can type in has to feel like a
   * spreadsheet — a spinner per cell would be worse than the round trip it
   * is hiding. A failure puts the old value back and says so once, above
   * the table, rather than forty times inside it.
   */
  const saveField = useCallback(async (row: Candidate, patch: Record<string, any>) => {
    const previous = rows;
    setRows((current) => current.map((c) => (c._id === row._id ? { ...c, ...patch } : c)));
    try {
      await HiringService.update(row._id, patch);
      setSaveError(null);
    } catch (err: any) {
      setRows(previous);
      setSaveError(err?.response?.data?.message || 'That change did not save.');
    }
  }, [rows]);

  /**
   * A grid cell, saved the way a spreadsheet cell is: the glyph changes
   * under the click, and the round trip catches up. The server still owns
   * stage derivation, so its answer for the stage is taken over ours.
   */
  const saveCheckpoint = useCallback(async (
    row: Candidate,
    key: string,
    body: { status: CheckpointStatus; state?: string | null; value?: string },
  ) => {
    const previous = rows;
    setRows((current) => current.map((c) => (c._id === row._id
      ? {
        ...c,
        checkpoints: {
          ...c.checkpoints,
          [key]: { status: body.status, state: body.state ?? null, value: body.value ?? c.checkpoints?.[key]?.value ?? null },
        },
      }
      : c)));
    try {
      const result = await HiringService.setCheckpoint(row._id, key, body);
      setRows((current) => current.map((c) => (c._id === row._id ? { ...c, stage: result.stage } : c)));
      setSaveError(null);
      refreshStalled(true);
    } catch (err: any) {
      setRows(previous);
      setSaveError(err?.response?.data?.message || 'That checkpoint did not change.');
    }
  }, [rows]);

  /**
   * The Status column writes. A stage pick is a manual placement — it
   * stands until a checkpoint finished later says otherwise, which is the
   * auto/manual split the old sheet's one dropdown was gesturing at. A
   * negative pick closes them, and a closed row leaves this view, exactly
   * as it always has.
   */
  const saveStage = useCallback(async (row: Candidate, nextStage: CandidateStage) => {
    const previous = rows;
    setRows((current) => current.map((c) => (c._id === row._id ? { ...c, stage: nextStage } : c)));
    try {
      await HiringService.setStage(row._id, nextStage);
      setSaveError(null);
      refreshStalled(true);
    } catch (err: any) {
      setRows(previous);
      setSaveError(err?.response?.data?.message || 'That stage did not change.');
    }
  }, [rows]);

  const closeAs = useCallback(async (row: Candidate, outcome: CandidateOutcome) => {
    const previous = rows;
    setRows((current) => current.filter((c) => c._id !== row._id));
    try {
      await HiringService.close(row._id, outcome);
      setSaveError(null);
      refreshStalled(true);
    } catch (err: any) {
      setRows(previous);
      setSaveError(err?.response?.data?.message || 'They were not closed.');
    }
  }, [rows]);

  /**
   * Bulk delete, from the grid's selection bar. Delete is for rows that
   * should never have existed — duplicates, test entries — and closing is
   * how a real candidate leaves; the grid's confirm dialog says as much.
   * The server restricts it to AO/CA either way.
   */
  const deleteRows = useCallback(async (targets: Candidate[]) => {
    const previous = rows;
    const ids = new Set(targets.map((t) => t._id));
    setRows((current) => current.filter((c) => !ids.has(c._id)));
    try {
      for (const target of targets) {
        // Sequential on purpose: one clear failure beats forty parallel ones.
        // eslint-disable-next-line no-await-in-loop
        await HiringService.remove(target._id);
      }
      setSaveError(null);
      refreshStalled(true);
    } catch (err: any) {
      setRows(previous);
      setSaveError(err?.response?.data?.message || 'Not everything was deleted — the list is back to how it was.');
      load();
    }
  }, [rows, load]);

  /** Same filing flow as the candidate page — a file is what makes it green. */
  const submitUpload = useCallback(async (file: File, expiresAt: string | null) => {
    if (!uploadFor) return;
    const { row, checkpoint } = uploadFor;
    const form = new FormData();
    form.append('file', file);
    if (checkpoint.documentTypeKey) form.append('documentTypeKey', checkpoint.documentTypeKey);
    form.append('label', checkpointLabel(checkpoint));
    if (expiresAt) form.append('expiresAt', expiresAt);

    setUploadBusy(true);
    setUploadError(null);
    try {
      await HiringService.uploadAttachment(row._id, form);
      if (expiresAt && checkpoint.documentTypeKey === 'mec') {
        await HiringService.update(row._id, { mecExpiresAt: expiresAt });
      }
      if (expiresAt && checkpoint.documentTypeKey === 'driver_license') {
        await HiringService.update(row._id, { licenseExpiresAt: expiresAt });
      }
      setUploadFor(null);
      refreshStalled(true);
      await load();
    } catch (err: any) {
      setUploadError(err?.response?.data?.message || 'That file did not upload.');
    } finally {
      setUploadBusy(false);
    }
  }, [uploadFor, load]);

  /**
   * "Needs follow-up" is the one screen whose whole job is to say what to
   * do, and it was the one screen that said least: the stalled endpoint
   * returned a seven-field projection, so the stage chip rendered empty
   * and This stage, Source and Last activity were all em-dashes. The
   * backend now decorates those rows like any other; these columns are
   * what the screen should have been showing on top of them.
   */
  /**
   * The to-do list.
   *
   * This was "Needs follow-up" and it answered a different question — who
   * has gone quiet — so somebody added this morning whose background check
   * link had not gone out appeared on no list at all, while plainly being
   * the most actionable person in the pipeline. It now lists everyone
   * whose next step is OURS, with gone-quiet as an ordering signal rather
   * than as the entry condition.
   */
  /**
   * How the to-do list is read, and by whom.
   *
   * The order it ships in answers "what should I do next"; the other two
   * answer questions it cannot. "Who is late" is a different list from
   * "what is due soonest", and somebody working through the chasing
   * wants only the rows that are with the candidate. None of it changes
   * which rows are ON the list — that is still every open candidate with
   * something outstanding.
   */
  const [todoSort, setTodoSort] = useState<TodoSort>('urgent');
  const [todoOwner, setTodoOwner] = useState<TodoOwner>('me');
  const myAccountId = useAuthStore((s) => s.account?._id) as string | undefined;
  const [todoAction, setTodoAction] = useState<string | null>(null);
  const [lateOnly, setLateOnly] = useState(false);
  /** Only the ones somebody marked worth watching. Both lists offer it. */
  const [starredOnly, setStarredOnly] = useState(false);

  const todo = useMemo(() => {
    const live = rows
      // A booked call belongs on this list whether or not the engine has
      // a next step for them: somebody agreed to talk at twelve, and that
      // is the most actionable thing in the pipeline.
      .filter((r) => r.outcome === 'open' && (r.nextAction || r.scheduledCallAt))
      .filter((r) => ownerMatches(r, todoOwner, myAccountId))
      // Matched on the SENTENCE, which is what the chip says — see
      // byAction below for why the label is not offered to anybody.
      .filter((r) => (todoAction
        ? (r.nextAction ? todoSentence(r.nextAction) === todoAction : false)
        : true))
      .filter((r) => (lateOnly ? (r.nextAction?.overdueDays || 0) > 0 : true))
      .filter((r) => (starredOnly ? !!r.starred : true));

    const late = (r: Candidate) => r.nextAction?.overdueDays || 0;
    const due = (r: Candidate) => (r.nextAction?.dueAt
      ? new Date(r.nextAction.dueAt).getTime()
      : Number.MAX_SAFE_INTEGER);

    if (todoSort === 'late') {
      return [...live].sort((a, b) => late(b) - late(a) || due(a) - due(b));
    }
    if (todoSort === 'due') {
      return [...live].sort((a, b) => due(a) - due(b));
    }
    if (todoSort === 'quiet') {
      return [...live].sort((a, b) => (b.daysSinceActivity ?? 0) - (a.daysSinceActivity ?? 0));
    }
    if (todoSort === 'name') {
      return [...live].sort((a, b) => candidateName(a).localeCompare(candidateName(b)));
    }

    // 'urgent' — the shipped order, and the one the page opens on.
    return [...live].sort((a, b) => {
      // Calls first, and among calls the most overdue first. This is the
      // one kind of work here with a TIME on it — everything else can be
      // done an hour later without anybody noticing.
      const aCall = callState(a.scheduledCallAt, new Date(), interviewed(a));
      const bCall = callState(b.scheduledCallAt, new Date(), interviewed(b));
      const aPressing = isPressing(aCall) ? 1 : 0;
      const bPressing = isPressing(bCall) ? 1 : 0;
      if (aPressing !== bPressing) return bPressing - aPressing;
      if (aPressing && bPressing) return aCall.rank - bCall.rank;

      // Then what is actually LATE, most overdue first. A step past its
      // own due date is a fact; "nobody has touched this in 9 days" is an
      // inference, and the fact wins.
      if (late(a) !== late(b)) return late(b) - late(a);

      const aStalled = stalledIds.has(a._id) ? 1 : 0;
      const bStalled = stalledIds.has(b._id) ? 1 : 0;
      if (aStalled !== bStalled) return bStalled - aStalled;
      return (b.daysSinceActivity ?? 0) - (a.daysSinceActivity ?? 0);
    });
  }, [rows, stalledIds, todoSort, todoOwner, myAccountId, todoAction, lateOnly, starredOnly]);

  /** The same work counted by what it is, so a batch can be done as a batch. */
  const byAction = useMemo(() => {
    const counts = new Map<string, number>();
    // Counted before the action filter, or picking one would leave a
    // single chip on screen with no way back to the others.
    // Counted within whose move it is, though: "12 × Send the link" over
    // a list of 3 is a count of somebody else's work.
    rows.filter((r) => r.outcome === 'open' && r.nextAction
      && ownerMatches(r, todoOwner, myAccountId)).forEach((r) => {
      // Named by the INSTRUCTION, never by the checkpoint's own label.
      // "8 × Interview completed" is a chip that says the opposite of
      // what it means: the step is CALLED "Interview completed" because
      // that is what it records once ticked, and those eight are on the
      // chip precisely because theirs is not. Same words as the column.
      const words = todoSentence(r.nextAction!);
      counts.set(words, (counts.get(words) || 0) + 1);
    });
    // forEach rather than spreading the Map: this project targets es5,
    // where iterating one needs downlevelIteration.
    const out: Array<[string, number]> = [];
    counts.forEach((n, label) => out.push([label, n]));
    return out.sort((a, b) => b[1] - a[1]).slice(0, 5);
  }, [rows, todoOwner, myAccountId]);

  /** How many people somebody is watching, for the chip that filters them. */
  const starredCount = useMemo(
    () => rows.filter((r) => r.outcome === 'open' && r.starred).length,
    [rows],
  );

  /** How many rows are past a step's own due date, for the Late filter. */
  const lateCount = useMemo(
    () => rows.filter((r) => r.outcome === 'open' && (r.nextAction?.overdueDays || 0) > 0
      && ownerMatches(r, todoOwner, myAccountId)).length,
    [rows, todoOwner, myAccountId],
  );

  const todoColumns: Column<Candidate>[] = [
    {
      key: 'name',
      header: 'Name',
      width: 210,
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={700} noWrap>{candidateName(row)}</Typography>
          {(row.email || row.phone) && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.email || row.phone}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'call',
      header: 'Call',
      width: 150,
      render: (row) => (
        <ScheduleCall
          variant="text"
          scheduledCallAt={row.scheduledCallAt}
          held={interviewed(row)}
          onSave={(iso) => saveField(row, { scheduledCallAt: iso })}
        />
      ),
    },
    {
      key: 'action',
      header: 'What to do',
      width: 260,
      render: (row) => {
        // A row can be here for its call alone, with nothing the engine
        // owes anybody — the call IS the job.
        if (!row.nextAction) {
          return (
            <Typography variant="body2" color="text.secondary" sx={{ cursor: 'default' }}>
              Talk to them
            </Typography>
          );
        }
        // The colour IS the information: red is a step past its own due
        // date, amber is due now, blue is ours to do, grey is somebody
        // else's move and running on time.
        const tone = nextTone(row.nextAction);
        const words = dueWords(row.nextAction);
        const party = withWhom(row.nextAction);
        // What it stepped past, in the tooltip rather than a third line —
        // the row height is shared by forty people.
        const waiting = waitingWords(row.nextAction);
        const behind = row.nextAction.daysBehind
          ? `About ${row.nextAction.daysBehind} days of waiting sit behind this`
          : '';
        return (
          <Tooltip
            title={[waiting && `Waiting on: ${waiting}`, behind].filter(Boolean).join(' · ')}
            placement="top-start"
          >
            <Box sx={{ cursor: 'default', minWidth: 0 }}>
              <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                <Box sx={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0, bgcolor: tone.color,
                }}
                />
                <Typography variant="body2" color="text.primary" noWrap>
                  {todoSentence(row.nextAction)}
                </Typography>
              </Stack>
              {!!words && (
                <Typography
                  variant="caption"
                  sx={{ color: tone.late ? statusTokens.danger : 'text.secondary', pl: 1.75 }}
                >
                  {words}
                  {party && ` · ${party}`}
                  {!!row.nextAction.waitingOn?.length
                    && ` · ${row.nextAction.waitingOn.map((w) => w.label.toLowerCase()).join(', ')} out`}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      key: 'stage',
      header: 'Stage',
      width: 130,
      hide: 'tablet',
      render: (row) => <OutcomeChip candidate={row} short />,
    },
    {
      key: 'progress',
      header: 'Progress',
      width: 110,
      hide: 'phone',
      render: (row) => (row.overall
        ? <StageMeter overall={row.overall} />
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
    {
      key: 'idle',
      header: 'Last activity',
      width: 110,
      align: 'right',
      render: (row) => <IdleDays days={row.daysSinceActivity} stalled={stalledIds.has(row._id)} />,
    },
  ];

  const editable = mode !== 'closed';

  const standardColumns: Column<Candidate>[] = [
    {
      key: 'name',
      header: 'Name',
      width: 190,
      render: (row) => (
        <Typography variant="body2" fontWeight={700} noWrap>{candidateName(row)}</Typography>
      ),
    },
    {
      key: 'stage',
      header: mode === 'closed' ? 'Outcome' : 'Stage',
      width: 130,
      render: (row) => <OutcomeChip candidate={row} short />,
    },
    {
      key: 'waiting',
      header: 'Waiting on',
      width: 160,
      hide: 'tablet',
      render: (row) => (row.outcome === 'open'
        ? (
          <Stack direction="row" alignItems="center" gap={0.75}>
            <MoveDot stage={row.stage} progress={row.progress} />
            <Typography variant="body2" color="text.secondary" noWrap>
              {MOVE_LABEL[moveFor(row.stage, row.progress)]}
            </Typography>
          </Stack>
        )
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
    {
      // One denominator for everybody. The per-stage meter put "4 of 5"
      // and "0 of 12" in the same column meaning different things, so
      // nothing down the list could be compared to anything.
      key: 'progress',
      header: 'Progress',
      width: 110,
      hide: 'phone',
      render: (row) => (row.overall
        ? <StageMeter overall={row.overall} />
        : <Typography variant="caption" color="text.disabled">—</Typography>),
    },
    {
      key: 'email',
      header: 'Email',
      width: 200,
      hide: 'tablet',
      render: (row) => (
        <EditableCell
          value={row.email}
          placeholder="Add an email"
          disabled={!editable}
          onSave={(next) => saveField(row, { email: next })}
        />
      ),
    },
    {
      key: 'phone',
      header: 'Mobile',
      width: 140,
      hide: 'tablet',
      render: (row) => (
        <EditableCell
          value={row.phone}
          placeholder="Add a number"
          disabled={!editable}
          onSave={(next) => saveField(row, { phone: next })}
        />
      ),
    },
    {
      key: 'start',
      header: 'Start date',
      width: 130,
      hide: 'tablet',
      render: (row) => (
        <EditableCell
          value={row.targetStartDate?.slice(0, 10)}
          type="date"
          placeholder="Not set"
          disabled={!editable}
          render={(v) => <Typography variant="body2" noWrap>{formatDate(v)}</Typography>}
          onSave={(next) => saveField(row, { targetStartDate: next || null })}
        />
      ),
    },
    {
      key: 'source',
      header: 'Source',
      width: 120,
      hide: 'tablet',
      render: (row) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {row.sourceDetail || SOURCE_LABEL[row.source] || '—'}
        </Typography>
      ),
    },
    {
      key: 'idle',
      header: mode === 'closed' ? 'Closed' : 'Last activity',
      width: 100,
      align: 'right',
      render: (row) => (mode === 'closed'
        ? <Typography variant="caption" color="text.secondary">{formatDate(row.closedAt)}</Typography>
        : <IdleDays days={row.daysSinceActivity} stalled={stalledIds.has(row._id)} />),
    },
  ];

  const { title, subtitle } = TITLES[mode];
  const data = rows;

  /**
   * Copy the message that fits, from the list.
   *
   * One click: the server says which message is due and writes it, and the
   * text goes on the clipboard. It records NOTHING — copying is not
   * sending, and a copy that ticks a checkpoint is a copy nobody can make
   * to read the wording back.
   */
  const copyMessage = useCallback(async (row: Candidate) => {
    try {
      const result = await HiringService.messages(row._id);
      const message = result.messages.find((m) => m.key === result.suggested)
        || result.messages[0];
      if (!message) { setSaveError('There is no message for this candidate.'); return; }

      await navigator.clipboard.writeText(message.body);
      setCopied(`${message.label} copied`);
    } catch {
      setSaveError('That message could not be copied.');
    }
  }, []);

  /**
   * Worth watching, or not.
   *
   * Optimistic and silent: a star is a judgement somebody makes while
   * reading down a list of forty, and a star that waits for a round trip
   * before it fills in is a star nobody trusts they pressed.
   */
  const star = useCallback(async (row: Candidate, starred: boolean) => {
    setRows((current) => current.map((r) => (r._id === row._id ? { ...r, starred } : r)));
    try {
      await HiringService.update(row._id, { starred });
    } catch {
      setRows((current) => current.map((r) => (r._id === row._id ? { ...r, starred: !starred } : r)));
      setSaveError('That star did not save.');
    }
  }, []);

  /** Their number, for the call that goes with the message. */
  const copyPhone = useCallback(async (row: Candidate) => {
    if (!row.phone) return;
    try {
      await navigator.clipboard.writeText(row.phone);
      setCopied(`${row.phone} copied`);
    } catch {
      setSaveError('That number could not be copied.');
    }
  }, []);

  return (
    // `fluid` for the grid only, and this is not a broken rule — it is the
    // escape PageBody documents for a working surface, the same exception
    // the document reader holds. Below the cap (~1330px viewport) fluid
    // and capped render identically, so the column still never jumps
    // between tabs on a laptop; on an ultrawide, the one screen that is a
    // spreadsheet gets the screen. To do and Closed are reading lists and
    // stay capped.
    <PageBody fluid={mode === 'all'}>
      <HiringPanel
        onChanged={load}
      />

      <PageHeader
        title={title}
        subtitle={mode === 'all'
          // The grid's own headers search and filter now, so the page
          // header stops narrating ("everyone currently being
          // considered") and just counts.
          ? (loading ? subtitle : `${data.length} candidates`)
          : (loading ? subtitle : mode === 'follow-up' && todoOwner === 'me'
            ? `${todo.length} waiting on you`
            : `${mode === 'follow-up' ? todo.length : data.length} · ${subtitle.toLowerCase()}`)}
      >
        {mode === 'closed' ? (
          <TextField
            size="small"
            placeholder="Find somebody…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
            }}
          />
        ) : mode === 'follow-up' ? (
          // Up here with the title, not down with the chips: the chips
          // are a variable-length row and the two controls kept being
          // pushed onto a line of their own behind them.
          <Stack direction="row" gap={1}>
            <TextField
              select
              size="small"
              label="Waiting on"
              value={todoOwner}
              onChange={(e) => { setTodoOwner(e.target.value as TodoOwner); setTodoAction(null); }}
              sx={{ width: 170 }}
            >
              {TODO_OWNERS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Sort by"
              value={todoSort}
              onChange={(e) => setTodoSort(e.target.value as TodoSort)}
              sx={{ width: 160 }}
            >
              {TODO_SORTS.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </TextField>
          </Stack>
        ) : undefined}
      </PageHeader>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Clipboards are invisible, so the row says what went onto it —
          and nothing beyond that, because nothing beyond that happened. */}
      <Snackbar
        open={!!copied}
        autoHideDuration={2500}
        onClose={() => setCopied(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setCopied(null)}>
          {copied}
        </Alert>
      </Snackbar>
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>
      )}

      {mode === 'follow-up' ? (
        <>
          {/* Read it another way. The chips are the work counted by what
              it IS — click one to do that batch as a batch — and Late is
              the same idea for the rows past their own due date. */}
          <Stack
            direction="row"
            gap={1}
            flexWrap="wrap"
            alignItems="center"
            sx={{ mb: 2 }}
          >
            {/* Somebody's own mark, as a filter. First in the row
                because "who am I watching" is the question people ask
                before "what is late". */}
            {starredCount > 0 && (
              <Chip
                size="small"
                icon={<StarIcon sx={{ fontSize: 15 }} />}
                label={`${starredCount} watching`}
                onClick={() => setStarredOnly((v) => !v)}
                variant={starredOnly ? 'filled' : 'outlined'}
                sx={starredOnly
                  ? { bgcolor: statusTokens.warn, color: '#fff', fontWeight: 700, '& .MuiChip-icon': { color: '#fff' } }
                  : { borderColor: statusTokens.warn, color: statusTokens.warn, '& .MuiChip-icon': { color: statusTokens.warn } }}
              />
            )}
            {lateCount > 0 && (
              <Chip
                size="small"
                label={`${lateCount} late`}
                onClick={() => setLateOnly((v) => !v)}
                variant={lateOnly ? 'filled' : 'outlined'}
                sx={lateOnly
                  ? { bgcolor: statusTokens.danger, color: '#fff', fontWeight: 700 }
                  : { borderColor: statusTokens.danger, color: statusTokens.danger }}
              />
            )}
            {byAction.map(([label, n]) => (
              <Chip
                key={label}
                size="small"
                label={`${n} × ${label}`}
                onClick={() => setTodoAction((current) => (current === label ? null : label))}
                variant={todoAction === label ? 'filled' : 'outlined'}
                color={todoAction === label ? 'primary' : 'default'}
              />
            ))}

          </Stack>
          <DataTable
            twoLine
            columns={todoColumns}
            rows={todo}
            rowKey={(row) => row._id}
            loading={loading}
            onRowClick={(row) => navigate(`/hiring/${row._id}`)}
            // Red is a call that is due or missed — the only deadline in
            // the pipeline. Amber is gone quiet, which orders this list but
            // does not decide who is on it.
            rowAccent={(row) => {
              const call = callState(row.scheduledCallAt, new Date(), interviewed(row));
              if (call.urgency === 'overdue' || call.urgency === 'now') return statusTokens.danger;
              if (call.urgency === 'soon') return statusTokens.warn;
              return stalledIds.has(row._id) ? statusTokens.warn : undefined;
            }}
            empty={
              <EmptyState
                icon={<PeopleIcon />}
                title={todoAction || lateOnly || starredOnly || (todoOwner !== 'all' && todoOwner !== 'me')
                  ? 'Nothing matches that'
                  : 'Nothing is waiting on you'}
                description={todoAction || lateOnly || starredOnly || (todoOwner !== 'all' && todoOwner !== 'me')
                  ? 'Clear the filters above to see the rest of the list.'
                  : todoOwner === 'me'
                    ? 'Everything open is with a candidate, a lab or a teammate — pick Anybody to see it.'
                    : "Every open candidate's next step belongs to somebody else."}
              />
            }
          />
        </>
      ) : mode === 'all' ? (
        rows.length === 0 && !loading ? (
          <EmptyState
            icon={<PeopleIcon />}
            title={search || stage !== 'all' ? 'Nothing matches that' : 'No candidates yet'}
            description="Add the first one from the panel on the left."
          />
        ) : (
          <>
            <CandidateGrid
              rows={rows}
              catalog={catalog}
              stalledIds={stalledIds}
              onOpen={(row) => navigate(`/hiring/${row._id}`)}
              onEditField={saveField}
              onSetStage={saveStage}
              onClose={closeAs}
              onDelete={deleteRows}
              onSetCheckpoint={saveCheckpoint}
              onUpload={(row, checkpoint) => { setUploadError(null); setUploadFor({ row, checkpoint }); }}
              onCopyMessage={copyMessage}
              onCopyPhone={copyPhone}
              onStar={star}
              starredOnly={starredOnly}
              onStarredOnly={setStarredOnly}
            />
            <UploadCheckpointDialog
              open={!!uploadFor}
              checkpoint={uploadFor?.checkpoint || null}
              busy={uploadBusy}
              error={uploadError}
              onClose={() => setUploadFor(null)}
              onUpload={submitUpload}
            />
          </>
        )
      ) : (
        <DataTable
          columns={standardColumns}
          rows={rows}
          rowKey={(row) => row._id}
          loading={loading}
          onRowClick={(row) => navigate(`/hiring/${row._id}`)}
          // The one place colour is spent on this screen: somebody nobody
          // has called back.
          rowAccent={(row) => (stalledIds.has(row._id) ? statusTokens.warn : undefined)}
          empty={
            <EmptyState
              icon={<PeopleIcon />}
              title="Nothing has closed yet"
              description="Candidates appear here once their pipeline ends, either way."
            />
          }
        />
      )}


    </PageBody>
  );
};

export default CandidateList;
